import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { AnnouncementsAdmin } from '../components/AnnouncementsAdmin';
import { useStore } from '../stores/store';
import { H2, Muted } from '../components/ui';

// Bandera emoji a partir del código ISO de país (regional indicators).
function ccFlag(cc: string): string {
  if (cc.length !== 2) return '🏳️';
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 8px;
`;
const Kpi = styled.div<{ $clickable?: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme, $clickable }) => ($clickable ? theme.colors.accent : theme.colors.borderStrong)};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px 8px;
  text-align: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  .n { font-size: 1.7rem; font-weight: 800; color: ${({ theme }) => theme.colors.accent}; }
  .l { font-size: 0.74rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .hint { font-size: 0.62rem; color: ${({ theme }) => theme.colors.accent}; margin-top: 2px; }
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  margin-bottom: 6px;
  font-size: 0.92rem;
  .k { color: ${({ theme }) => theme.colors.textMuted}; }
  strong { font-weight: 800; }
`;
const SortBar = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  button {
    flex: 1;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textMuted};
    border-radius: 8px;
    padding: 7px;
    font-size: 0.82rem;
    font-weight: 700;
  }
  button.on {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Logout = styled.button`
  width: 100%;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  color: ${({ theme }) => theme.colors.textMuted};
  border-radius: 10px;
  padding: 12px;
  margin-top: 18px;
`;

export function Uso() {
  const navigate = useNavigate();
  const adminKey = useStore((s) => s.adminKey);
  const clearAdmin = useStore((s) => s.clearAdmin);
  const [showUsers, setShowUsers] = useState(false);
  const [sortBy, setSortBy] = useState<'accesos' | 'paginas' | 'minutos'>('accesos');

  useEffect(() => {
    if (!adminKey) navigate('/acceso', { replace: true });
  }, [adminKey, navigate]);

  const state = useAsync(
    () => api.trackSummary(adminKey),
    [adminKey],
    adminKey ? `/track/summary?key=${encodeURIComponent(adminKey)}` : undefined
  );

  if (!adminKey) return null;

  return (
    <>
      <H2>Estadísticas de uso</H2>
      <Async state={state}>
        {(d) => (
          <>
            <Grid>
              <Kpi $clickable onClick={() => setShowUsers((v) => !v)}>
                <div className="n">{d.usuariosUnicos ?? 0}</div>
                <div className="l">usuarios distintos</div>
                <div className="hint">{showUsers ? 'ocultar lista ▲' : 'ver lista ▼'}</div>
              </Kpi>
              <Kpi><div className="n">{d.ingresos ?? 0}</div><div className="l">visitas</div></Kpi>
              <Kpi><div className="n">{d.paginasVistas ?? 0}</div><div className="l">páginas vistas</div></Kpi>
              <Kpi><div className="n">{d.minutosTotales ?? 0}</div><div className="l">minutos totales</div></Kpi>
            </Grid>

            {showUsers && (
              <>
                <H2>Usuarios</H2>
                <SortBar>
                  {(['accesos', 'paginas', 'minutos'] as const).map((k) => (
                    <button key={k} className={sortBy === k ? 'on' : ''} onClick={() => setSortBy(k)}>
                      {k === 'accesos' ? 'Accesos' : k === 'paginas' ? 'Páginas' : 'Minutos'}
                    </button>
                  ))}
                </SortBar>
                {[...(d.usuarios ?? [])]
                  .sort((a, b) => b[sortBy] - a[sortBy])
                  .map((u, i) => (
                    <Row key={i}>
                      <span>{u.nick}</span>
                      <span>
                        <strong>{u.accesos}</strong> acc · <strong>{u.paginas}</strong> págs ·{' '}
                        <strong>{u.minutos}</strong> min
                      </span>
                    </Row>
                  ))}
                {(d.usuarios ?? []).length === 0 && <Muted>Sin usuarios todavía.</Muted>}
              </>
            )}

            <H2>Por país</H2>
            {Object.entries(d.porPais ?? {}).map(([cc, n]) => (
              <Row key={cc}>
                <span>{ccFlag(cc)} {cc}</span>
                <strong>{n}</strong>
              </Row>
            ))}
            {Object.keys(d.porPais ?? {}).length === 0 && <Muted>Sin datos de país todavía.</Muted>}

            <H2>Páginas más vistas</H2>
            {Object.entries(d.paginasTop ?? {}).map(([path, n]) => (
              <Row key={path}>
                <span>{path || '/'}</span>
                <strong>{n}</strong>
              </Row>
            ))}

            <H2>Por día</H2>
            {Object.entries(d.porDia ?? {}).map(([day, v]) => (
              <Row key={day}>
                <span className="k">{day}</span>
                <span>
                  <strong>{v.sessions}</strong> ingresos · <strong>{v.minutes}</strong> min
                </span>
              </Row>
            ))}
            {Object.keys(d.porDia ?? {}).length === 0 && <Muted>Sin datos todavía.</Muted>}
          </>
        )}
      </Async>

      <AnnouncementsAdmin adminKey={adminKey} />

      <Logout onClick={clearAdmin}>Cerrar acceso en este dispositivo</Logout>
    </>
  );
}
